import { useEffect, useState } from 'react';

const getInitialState = (query: string, defaultState?: boolean) => {
    // Prevent a React hydration mismatch when a default value is provided by not defaulting to window.matchMedia(query).matches.
    if (defaultState !== undefined) {
        return defaultState;
    }

    if (typeof window !== 'undefined') {
        return window.matchMedia(query).matches;
    }

    return false;
};

const breakpoints = {
    'sm': '(min-width: 640px)',
    'md': '(min-width: 768px)',
    'lg': '(min-width: 1024px)',
    'xl': '(min-width: 1280px)',
    '2xl': '(min-width: 1536px)',

    'mobile': '(max-width: 768px)',
    'mobile-and-tablet': '(max-width: 1024px)',
    'desktop': '(min-width: 1024px)'
} as const;

type Breakpoint = keyof typeof breakpoints;

export const useMediaQuery = (breakpoint: Breakpoint, defaultState?: boolean) => {
    const query = breakpoints[breakpoint];

    const [state, setState] = useState(getInitialState(query, defaultState));

    useEffect(() => {
        let mounted = true;
        const mql = window.matchMedia(query);
        const onChange = () => {
            if (!mounted) {
                return;
            }
            setState(!!mql.matches);
        };

        mql.addEventListener('change', onChange);
        setState(mql.matches);

        return () => {
            mounted = false;
            mql.removeEventListener('change', onChange);
        };
    }, [query]);

    return state;
};
